// Centralized CompleteRegistration tracking. One source of truth for
// content_name / content_category / lead_source / currency, plus auto-attached
// UTM attribution. Fires BOTH the GTM dataLayer push and the direct Meta Pixel
// call, so Meta Test Events show submissions regardless of GTM trigger config.
import { fbqTrack, pushDataLayerEvent } from "./analytics";
import { getAttributionUtms } from "./utmAttribution";

/**
 * Canonical lead-source identifiers. Keep this list small and stable —
 * these strings flow into Meta Custom Audiences and GHL routing.
 */
export type LeadSource =
  | "lead-magnet-hero"
  | "lead-magnet-exit-intent"
  | "growth-checkup"
  | "growth-analysis";

/**
 * Canonical content_name per funnel — what Meta groups conversions by.
 */
export const LEAD_CONTENT: Record<LeadSource, { content_name: string; content_category: string }> = {
  "lead-magnet-hero": {
    content_name: "New Patient Leak Checklist",
    content_category: "Lead Magnet",
  },
  "lead-magnet-exit-intent": {
    content_name: "New Patient Leak Checklist",
    content_category: "Lead Magnet",
  },
  "growth-checkup": {
    content_name: "Growth Checkup",
    content_category: "Checkup Request",
  },
  "growth-analysis": {
    content_name: "Growth Analysis",
    content_category: "Score Quiz",
  },
};

interface ExtraProps {
  /** Optional numeric value (e.g. quiz score) sent on both pixel + dataLayer. */
  value?: number;
  /** Anything else to merge into the payload (e.g. magnet slug). */
  extra?: Record<string, unknown>;
}

/**
 * Fire CompleteRegistration on GTM dataLayer + Meta Pixel with consistent
 * fields and current UTM attribution attached.
 */
export function trackCompleteRegistration(source: LeadSource, opts: ExtraProps = {}): void {
  const { content_name, content_category } = LEAD_CONTENT[source];
  const utms = getAttributionUtms();
  const payload: Record<string, unknown> = {
    content_name,
    content_category,
    lead_source: source,
    currency: "USD",
    ...(typeof opts.value === "number" ? { value: opts.value } : {}),
    ...utms,
    ...(opts.extra ?? {}),
  };

  pushDataLayerEvent("CompleteRegistration", payload);
  fbqTrack("CompleteRegistration", payload);
}
